import {
  Fragment,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  Alert,
  Box,
  Button,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  IMMUTABLE_RQ,
  fetchStaticJson,
  type PointersFile,
} from "@/api/staticContent";
import type { IdSource } from "./manifestReference";
import { CopyableId } from "./CopyableId";

/**
 * Type-a-partial-name lookups for /hldebug/provision-user/manifest. Each one
 * reads the same published content file the provisioner validates against, so
 * an id found here is one the manifest will accept on this environment.
 */

/** Enough to scan; a narrower search beats a longer table. */
const MAX_ROWS = 25;

export interface LookupProps {
  pointers: PointersFile;
  districtId?: string;
}

interface Row {
  id: string;
  name: string;
  /** Extra columns, in the order of `columns`. */
  cells?: ReactNode[];
  action?: ReactNode;
  /** More ids this row answers to, e.g. a school to its program's id. */
  searchIds?: string[];
  /** Search on this name instead, e.g. a school by its program's name. */
  searchName?: string;
  /** Shown full width beneath the row, e.g. a hat's options once opened. */
  expanded?: ReactNode;
}

const mono = { fontFamily: "monospace" } as const;

const useStatic = <T,>(path?: string) =>
  useQuery({
    queryKey: ["qa-manifest-reference", "file", path],
    enabled: !!path,
    ...IMMUTABLE_RQ,
    queryFn: () => fetchStaticJson<T>(path!),
  });

/** Loads every plan's copy of a plan-scoped file. */
const useStaticPerPlan = <T,>(map?: Record<string, string>) => {
  const entries = Object.entries(map ?? {});
  const results = useQueries({
    queries: entries.map(([, path]) => ({
      queryKey: ["qa-manifest-reference", "file", path],
      ...IMMUTABLE_RQ,
      queryFn: () => fetchStaticJson<T>(path),
    })),
  });
  return {
    files: results.flatMap((r, i) =>
      r.data ? [{ plan: entries[i][0], data: r.data }] : [],
    ),
    isLoading: results.some((r) => r.isLoading),
    error: results.find((r) => r.error)?.error as Error | undefined,
  };
};

/** Leading zeros don't distinguish ids: badge 12 is written 0012. */
const normalizeId = (id: string) => id.toLowerCase().replace(/^0+(?=.)/, "");

/**
 * How well a row answers a query, or -1 for not at all: an exact id first,
 * then an id that starts with it, then a name containing it (ignoring case).
 */
const matchRank = (row: Row, query: string): number => {
  const q = query.trim().toLowerCase();
  if (!q) return 2;
  const nq = normalizeId(q);
  const ids = [row.id, ...(row.searchIds ?? [])].map(normalizeId);
  if (ids.includes(nq)) return 0;
  if (ids.some((id) => id.startsWith(nq))) return 1;
  if ((row.searchName ?? row.name).toLowerCase().includes(q)) return 2;
  return -1;
};

/** The rows that answer a query, best matches first. */
const search = (rows: Row[], query: string): Row[] =>
  rows
    .map((row) => ({ row, rank: matchRank(row, query) }))
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .map(({ row }) => row);

const SearchTable = ({
  label,
  rows,
  columns = [],
  isLoading,
  error,
  idHeader = "id",
}: {
  label: string;
  rows: Row[];
  columns?: string[];
  isLoading?: boolean;
  error?: Error | null;
  idHeader?: string;
}) => {
  const [query, setQuery] = useState("");
  const found = useMemo(() => search(rows, query), [rows, query]);
  const hasAction = rows.some((row) => row.action);

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        label={label}
        placeholder="Type part of a name, or an id"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mb: 1 }}
      />
      {error && <Alert severity="error">{error.message}</Alert>}
      {isLoading && !rows.length ? (
        <Typography variant="body2">Loading…</Typography>
      ) : (
        query.trim() && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{idHeader}</TableCell>
                  <TableCell>Name</TableCell>
                  {columns.map((column) => (
                    <TableCell key={column}>{column}</TableCell>
                  ))}
                  {hasAction && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {found.slice(0, MAX_ROWS).map((row, i) => (
                  // A school offered by two programs appears twice.
                  <Fragment key={`${row.id}-${i}`}>
                    <TableRow>
                      <TableCell>
                        <CopyableId id={row.id} />
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      {row.cells?.map((cell, i) => (
                        <TableCell key={columns[i]}>{cell}</TableCell>
                      ))}
                      {hasAction && <TableCell>{row.action}</TableCell>}
                    </TableRow>
                    {row.expanded && (
                      <TableRow>
                        <TableCell
                          colSpan={2 + columns.length + (hasAction ? 1 : 0)}
                          sx={{ bgcolor: "action.hover" }}
                        >
                          {row.expanded}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary">
              {found.length > MAX_ROWS
                ? `First ${MAX_ROWS} of ${found.length} matches — type more to narrow it`
                : `${found.length} match${found.length === 1 ? "" : "es"}`}
            </Typography>
          </>
        )
      )}
    </Box>
  );
};

/** Merges a plan-scoped list into one row per id, noting which plans have it. */
const mergeByPlan = <T extends { id: string }>(
  files: { plan: string; items: T[] }[],
  toRow: (item: T, plans: string[]) => Row,
): Row[] => {
  const byId = new Map<string, { item: T; plans: string[] }>();
  for (const { plan, items } of files) {
    for (const item of items) {
      const seen = byId.get(item.id);
      if (seen) seen.plans.push(plan);
      else byId.set(item.id, { item, plans: [plan] });
    }
  }
  return [...byId.values()].map(({ item, plans }) => toRow(item, plans));
};

const Plans = ({ plans }: { plans: string[] }) => (
  <Typography variant="caption" color="text.secondary">
    {plans.join(", ")}
  </Typography>
);

// ── Hats ──────────────────────────────────────────────────────────────────

interface HatSummary {
  id: string;
  displayName: string;
  detailPath: string;
  /** Pathway ids — the index publishes a list, not one id. */
  msPathway?: string[] | null;
}

interface NamedId {
  id: string;
  name: string;
}

interface HatDetail {
  msKeysToSuccess?: NamedId[];
  msPowerSkills?: NamedId[];
  msProSkills?: NamedId[];
  msPopularPaths?: NamedId[];
  msOtherRoutes?: NamedId[];
}

/** Every id a Climb Plan built on this hat can take for its hat-scoped keys. */
const HatOptions = ({ hat }: { hat: HatSummary }) => {
  const { data, isLoading, error } = useStatic<HatDetail>(hat.detailPath);
  if (isLoading) return <Typography variant="body2">Loading…</Typography>;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;
  const rows: Row[] = (
    [
      ["goal=", "Key to Success", data?.msKeysToSuccess],
      ["best= / improve=", "PowerSkill", data?.msPowerSkills],
      ["excited=", "ProSkill", data?.msProSkills],
      ["paths=", "Popular path", data?.msPopularPaths],
      ["paths=", "Other route", data?.msOtherRoutes],
    ] as const
  ).flatMap(([key, kind, items]) =>
    (items ?? []).map((item) => ({
      id: item.id,
      name: item.name,
      cells: [
        <Box key="key" component="span" sx={mono}>
          {key}
        </Box>,
        kind,
      ],
    })),
  );
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Climb Plan options for {hat.displayName}
      </Typography>
      <AllRows rows={rows} columns={["Key", "Kind"]} filterable={false} />
    </Box>
  );
};

const HatSearch = ({ pointers }: LookupProps) => {
  const { files, isLoading, error } = useStaticPerPlan<{ hats: HatSummary[] }>(
    pointers.hatIndex,
  );
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (hatId: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(hatId)) next.add(hatId);
      return next;
    });

  const rows = mergeByPlan(
    files.map(({ plan, data }) => ({ plan, items: data.hats ?? [] })),
    (hat, plans) => ({
      id: hat.id,
      name: hat.displayName,
      cells: [
        hat.msPathway?.join(", ") || "—",
        <Plans key="plans" plans={plans} />,
      ],
      action: (
        <Link
          component="button"
          variant="body2"
          onClick={() => toggle(hat.id)}
          sx={{ whiteSpace: "nowrap" }}
        >
          {open.has(hat.id) ? "Hide hat options" : "Show hat options"}
        </Link>
      ),
      expanded: open.has(hat.id) ? <HatOptions hat={hat} /> : undefined,
    }),
  );
  return (
    <SearchTable
      label="Hat name or id"
      rows={rows}
      columns={["Pathway", "Plans"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

/** A short list shown whole, with the same search box above it. */
const AllRows = ({
  rows,
  columns,
  filterable = true,
}: {
  rows: Row[];
  columns: string[];
  filterable?: boolean;
}) => {
  const [query, setQuery] = useState("");
  const found = search(rows, query);
  return (
    <Box>
      {filterable && (
        <TextField
          size="small"
          fullWidth
          label="Filter by name or id"
          placeholder="Type part of a name, or an id"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ mb: 1 }}
        />
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>id</TableCell>
            <TableCell>Name</TableCell>
            {columns.map((column) => (
              <TableCell key={column}>{column}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* The index too: a hat can list the same path as both a popular
              path and an other route. */}
          {found.map((row, index) => (
            <TableRow key={`${row.id}-${row.name}-${index}`}>
              <TableCell>
                <CopyableId id={row.id} />
              </TableCell>
              <TableCell>{row.name}</TableCell>
              {row.cells?.map((cell, i) => (
                <TableCell key={columns[i]}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ── District programs and high schools ────────────────────────────────────

interface DistrictSettingsFile {
  programs?: {
    id: string;
    name: string;
    status: string;
    schools?: { id: string; name: string; feederMiddleSchoolIds?: string[] }[];
  }[];
}

const DistrictPrograms = ({ pointers, districtId }: LookupProps) => {
  const path = districtId ? pointers.districtSettings?.[districtId] : undefined;
  const { data, isLoading, error } = useStatic<DistrictSettingsFile>(path);

  if (!path) {
    return (
      <Alert severity="info">
        {districtId ?? "Your district"} has no published district settings, so
        every climb plan here gets NO_DISTRICT_SETTINGS and takes no program= or
        school=.
      </Alert>
    );
  }

  // One row per program and per school. Both search on the program, so typing
  // a program name lists it with every school that offers it.
  const programs = (data?.programs ?? []).filter((p) => p.status === "active");
  const rows: Row[] = programs.flatMap((program) => [
    {
      id: program.id,
      name: program.name,
      cells: [
        <Box key="k" component="span" sx={mono}>
          program=
        </Box>,
        "—",
      ],
    },
    ...(program.schools ?? []).map((school) => ({
      id: school.id,
      name: school.name,
      searchIds: [program.id],
      searchName: program.name,
      cells: [
        <Box key="k" component="span" sx={mono}>
          school=
        </Box>,
        <span key="program">
          {program.name} (<CopyableId id={program.id} />)
          {school.feederMiddleSchoolIds?.length
            ? ` · feeders ${school.feederMiddleSchoolIds.join(", ")}`
            : ""}
        </span>,
      ],
    })),
  ]);

  if (isLoading) return <Typography variant="body2">Loading…</Typography>;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;
  return (
    <SearchTable
      label="Program name, or a program or school id"
      rows={rows}
      columns={["Key", "Program · feeder middle schools"]}
    />
  );
};

// ── Clusters, pathways and their content blocks ───────────────────────────

interface ClusterIndexFile {
  clusters: {
    id: string;
    name: string;
    detailPath: string;
    pathways?: { id: string; name: string; detailPath: string }[];
  }[];
}

interface ContentBlock {
  id: string;
  blockType?: string;
  contentType?: string;
  blockName?: string | null;
  ladder?: { id?: string; name?: string } | null;
  activity?: { id?: string; name?: string } | null;
}

/** What a content block is called, which the published file never says outright. */
const blockLabel = (block: ContentBlock) =>
  block.ladder?.name ??
  block.activity?.name ??
  block.blockName ??
  [block.contentType, block.blockType].filter(Boolean).join(" · ");

const ContentBlocks = ({ path, verb }: { path: string; verb: string }) => {
  const { data, isLoading, error } = useStatic<{ content?: ContentBlock[] }>(
    path,
  );
  if (isLoading) return <Typography variant="body2">Loading…</Typography>;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;
  const rows = (data?.content ?? []).map((block) => ({
    id: String(block.id),
    name: blockLabel(block),
    cells: [block.blockType ?? "—"],
  }));
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        contentId for {verb}
      </Typography>
      <AllRows rows={rows} columns={["Block type"]} />
    </Box>
  );
};

const ClusterSearch = ({ pointers }: LookupProps) => {
  const { files, isLoading, error } = useStaticPerPlan<ClusterIndexFile>(
    pointers.clusterPathwayIndexes,
  );
  const [picked, setPicked] = useState<{
    kind: "cluster" | "pathway";
    id: string;
    name: string;
    path: string;
  } | null>(null);

  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: Row[] = [];
    for (const { data } of files) {
      for (const cluster of data.clusters ?? []) {
        const pick = (
          kind: "cluster" | "pathway",
          id: string,
          name: string,
          path: string,
        ) => (
          <Button
            size="small"
            color="primary"
            onClick={() => setPicked({ kind, id, name, path })}
          >
            Content
          </Button>
        );
        if (!seen.has(`c${cluster.id}`)) {
          seen.add(`c${cluster.id}`);
          out.push({
            id: cluster.id,
            name: cluster.name,
            cells: ["cluster", "—"],
            action: pick(
              "cluster",
              cluster.id,
              cluster.name,
              cluster.detailPath,
            ),
          });
        }
        for (const pathway of cluster.pathways ?? []) {
          if (seen.has(`p${pathway.id}`)) continue;
          seen.add(`p${pathway.id}`);
          out.push({
            id: pathway.id,
            name: pathway.name,
            cells: [
              "pathway",
              <span key="cluster">
                {cluster.name} (<CopyableId id={cluster.id} />)
              </span>,
            ],
            action: pick(
              "pathway",
              pathway.id,
              pathway.name,
              pathway.detailPath,
            ),
          });
        }
      }
    }
    return out;
  }, [files]);

  return (
    <Stack spacing={2}>
      <SearchTable
        label="Cluster or pathway name, or id"
        rows={rows}
        columns={["Kind", "Cluster"]}
        isLoading={isLoading}
        error={error}
      />
      {picked && (
        <Box>
          <Typography variant="subtitle2">
            Content blocks in {picked.kind} {picked.name} (
            <CopyableId id={picked.id} />)
          </Typography>
          <ContentBlocks
            path={picked.path}
            verb={
              picked.kind === "cluster" ? "cluster-content" : "pathway-content"
            }
          />
        </Box>
      )}
    </Stack>
  );
};

// ── Flat global files ─────────────────────────────────────────────────────

const ConnectorActivitySearch = ({ pointers }: LookupProps) => {
  const { data, isLoading, error } = useStatic<{
    connectorActivities: {
      id: string;
      name: string;
      category?: { name?: string };
    }[];
  }>(pointers.connectorActivities);
  const rows = (data?.connectorActivities ?? []).map((a) => ({
    id: a.id,
    name: a.name,
    cells: [a.category?.name ?? "—"],
  }));
  return (
    <SearchTable
      label="Activity name or id"
      rows={rows}
      columns={["Category"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

const PerkQuirkSearch = ({ pointers }: LookupProps) => {
  const { data, isLoading, error } = useStatic<{
    perksQuirks: { id: string; name: string; type: string }[];
  }>(pointers.perksQuirks);
  const rows = (data?.perksQuirks ?? []).map((pq) => ({
    id: pq.id,
    name: pq.name,
    cells: [pq.type],
  }));
  return (
    <SearchTable
      label="Perk or quirk text, or id"
      rows={rows}
      columns={["Type"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

const AwardSearch = ({ pointers }: LookupProps) => {
  const { data, isLoading, error } = useStatic<{
    awards: { id: string; name: string; title?: string; awardType?: string }[];
  }>(pointers.awards);
  const rows = (data?.awards ?? []).map((award) => ({
    id: award.id,
    name: award.title ? `${award.name} — ${award.title}` : award.name,
    cells: [award.awardType ?? "—"],
  }));
  return (
    <SearchTable
      label="Award name or id"
      rows={rows}
      columns={["Type"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

const ProductSearch = ({ pointers }: LookupProps) => {
  const { data, isLoading, error } = useStatic<{
    products: { code: string; name: string; learnerPlanCodes?: string[] }[];
  }>(pointers.productIndex);
  const rows = (data?.products ?? []).map((product) => ({
    id: product.code,
    name: product.name,
    cells: [<Plans key="plans" plans={product.learnerPlanCodes ?? []} />],
  }));
  return (
    <SearchTable
      label="Product name or code"
      idHeader="code"
      rows={rows}
      columns={["Climber content plans"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

// ── Plan-scoped files ─────────────────────────────────────────────────────

const BadgeSearch = ({ pointers }: LookupProps) => {
  const { files, isLoading, error } = useStaticPerPlan<{
    badges: { id: string; name: string; type?: string }[];
  }>(pointers.badgeIndexes);
  const rows = mergeByPlan(
    files.map(({ plan, data }) => ({ plan, items: data.badges ?? [] })),
    (badge, plans) => ({
      // like-perkquirk takes the zero-padded form.
      id: String(badge.id).padStart(4, "0"),
      name: badge.name,
      cells: [badge.type ?? "—", <Plans key="plans" plans={plans} />],
    }),
  );
  return (
    <SearchTable
      label="Badge name or id"
      idHeader="badge="
      rows={rows}
      columns={["Type", "Plans"]}
      isLoading={isLoading}
      error={error}
    />
  );
};

interface LearningIndexFile {
  courses?: { id: string; name: string }[];
  climbs?: { id: string; name: string }[];
  ladders?: { id: string; name: string; ladderType?: string }[];
}

const LEARNING_KINDS = [
  { key: "ladders", verb: "ladder" },
  { key: "climbs", verb: "climb" },
  { key: "courses", verb: "course" },
] as const;

const LearningSearch = ({ pointers }: LookupProps) => {
  const [kind, setKind] =
    useState<(typeof LEARNING_KINDS)[number]["key"]>("ladders");
  const { files, isLoading, error } = useStaticPerPlan<LearningIndexFile>(
    pointers.learningIndexes,
  );
  const verb = LEARNING_KINDS.find((k) => k.key === kind)!.verb;
  const rows = mergeByPlan(
    files.map(({ plan, data }) => ({ plan, items: data[kind] ?? [] })),
    (item, plans) => ({
      id: item.id,
      name: item.name,
      cells: [
        ("ladderType" in item && item.ladderType) || verb,
        <Plans key="plans" plans={plans} />,
      ],
    }),
  );
  return (
    <Stack spacing={1}>
      <TextField
        select
        size="small"
        label="Kind"
        value={kind}
        onChange={(event) => setKind(event.target.value as typeof kind)}
        sx={{ width: 200 }}
      >
        {LEARNING_KINDS.map((k) => (
          <MenuItem key={k.key} value={k.key}>
            {k.verb}
          </MenuItem>
        ))}
      </TextField>
      <SearchTable
        key={kind}
        label={`${verb[0].toUpperCase()}${verb.slice(1)} name or id`}
        rows={rows}
        columns={["Type", "Plans"]}
        isLoading={isLoading}
        error={error}
      />
    </Stack>
  );
};

/** The interactive lookup for each id source that has one. */
export const ID_LOOKUPS: Partial<Record<IdSource, ComponentType<LookupProps>>> =
  {
    hatIndex: HatSearch,
    districtSettings: DistrictPrograms,
    clusterPathways: ClusterSearch,
    connectorActivities: ConnectorActivitySearch,
    perksQuirks: PerkQuirkSearch,
    badges: BadgeSearch,
    learning: LearningSearch,
    awards: AwardSearch,
    products: ProductSearch,
  };
